import React from 'react';

import { cn } from '../lib/utils';
import type { EnvironmentVariable } from '../types/api';

interface EnvironmentVariablesListProps {
  variables: EnvironmentVariable[];
  /** Rendered in place of the list when the version has no variables. */
  empty?: React.ReactNode;
  className?: string;
}

/**
 * The environment variables a version hands its container, read-only: the
 * member workbench's version details and System Admin's review panel.
 *
 * The API never sends a secret's value, so there is nothing here to mask or
 * reveal: a secret is shown by name and says so. The matching edit control is
 * `EnvironmentVariablesField`.
 */
export const EnvironmentVariablesList: React.FC<EnvironmentVariablesListProps> = ({
  variables,
  empty = null,
  className,
}) => {
  if (variables.length === 0) return <>{empty}</>;
  return (
    <ul className={cn('space-y-1', className)}>
      {variables.map((variable) => (
        <li
          key={`${variable.source}:${variable.name}`}
          className="flex min-w-0 flex-wrap items-baseline gap-x-2 gap-y-0.5"
        >
          <span className="font-mono text-xs font-medium">{variable.name}</span>
          <EnvironmentValue variable={variable} />
        </li>
      ))}
    </ul>
  );
};

const EnvironmentValue: React.FC<{ variable: EnvironmentVariable }> = ({ variable }) => {
  if (variable.source === 'kubernetes_secret') {
    return <span className="text-xs text-muted-foreground">From a Kubernetes Secret</span>;
  }
  if (variable.secret) {
    return <span className="text-xs text-muted-foreground">Secret, value hidden</span>;
  }
  if (!variable.value) {
    return <span className="text-xs text-muted-foreground">Empty</span>;
  }
  return <span className="min-w-0 break-all font-mono text-xs text-muted-foreground">{variable.value}</span>;
};
